import {Component, Input} from '@angular/core';
import {IFilter, IFilterGroup} from '../../../../../features/settings-new/models/general/advanced-filter-group.interface';
import {ColDef} from 'ag-grid-community';
import {getHumanReadableIdentifier} from '../../../../shared-utils/primitives/human-readablility.utils';
import {
  FilterTypesBoolean,
  FilterTypesDate,
  FilterTypesNumber,
  FilterTypesPresence,
  FilterTypesString,
  FilterTypesStringArray,
  FilterTypeTypesenseEnum,
} from '../../../../../features/settings-new/models/general/advanced-filter-types.enum';
import {IFilterType} from '../../../../../features/settings-new/models/general/advanced-filter-types.interface';
import {
  getAdvancedFilterColumnTypeLabel,
  resolveAdvancedFilterColumnType,
} from '../../../../../features/settings-new/models/general/advanced-filters.utils';
import {Store} from '@ngrx/store';
import {
  deleteAdvancedFilterGroupFilter,
  setFilterAfterValueChange,
} from '../../../../../features/core/store-shared-filter/shared-filter.actions';
import {getDeepCopyOfObject} from '../../../../shared-utils/object/object.utils';
import {ISelectOption} from '../../../../shared-models/custom-select/custom-select';
import {
  ADVANCED_FILTER_CLIENT_SIDE_IS_EMPTY_SLOW_MESSAGE,
  isClientSideEndsWithAdvancedFilter,
  isClientSideIsEmptyAdvancedFilter,
} from '../../../../shared-utils/typesense-utils/typesense.utils';

@Component({
  selector: 'app-shared-modal-filters-advanced-group-item',
  templateUrl: './shared-modal-filters-advanced-group-item.component.html',
  styleUrls: ['./shared-modal-filters-advanced-group-item.component.scss'],
  standalone: false,
})
export class SharedModalFiltersAdvancedGroupItemComponent {

  @Input() allowRemove = true;

  filterTypeString = FilterTypesString;
  filterTypeStringArray = FilterTypesStringArray;
  filterTypeNumber = FilterTypesNumber;
  filterTypeBoolean = FilterTypesBoolean;
  filterTypeDate = FilterTypesDate;

  colDefs: ColDef[];
  filter: IFilter;
  filterTypes: IFilterType[] = [];
  rangeMin = '';
  rangeMax = '';

  protected readonly clientSideIsEmptySlowMessage = ADVANCED_FILTER_CLIENT_SIDE_IS_EMPTY_SLOW_MESSAGE;

  constructor(
    private readonly store: Store,
  ) {
  }

  get getFilterTypeInputCount(): 'single' | 'multiple' | 'none' {
    const found = this.filterTypes.find((type: IFilterType): boolean => type.description === this.filter.filterType);
    return found?.inputs ?? 'single';
  }

  get requiresFilterValueInput(): boolean {
    return this.getFilterTypeInputCount !== 'none';
  }

  get getFilterColumnType(): string {
    let columnType = this.filter.columnType;
    if (this.filter.filterType === FilterTypeTypesenseEnum.xDaysLessThan || this.filter.filterType === FilterTypeTypesenseEnum.xDaysGreaterThan) {
      columnType = 'number';
    }
    return columnType;
  }

  get columnTypeLabel(): string {
    if (!this.filter?.column) {
      return '';
    }
    return getAdvancedFilterColumnTypeLabel(resolveAdvancedFilterColumnType(this.filter.column, this.filter.columnType));
  }

  get showClientSideIsEmptyWarning(): boolean {
    return isClientSideIsEmptyAdvancedFilter(this.filter) || isClientSideEndsWithAdvancedFilter(this.filter);
  }

  get columnOptions(): Record<string, ISelectOption> {
    const options: Record<string, ISelectOption> = {};
    this.colDefs?.forEach((col: ColDef): void => {
      options[col.field] = {
        name: col.headerName,
        selected: col.field === this.filter?.column,
        disabled: false,
      };
    });
    return options;
  }

  get filterTypeOptions(): Record<string, ISelectOption> {
    const options: Record<string, ISelectOption> = {};
    this.filterTypes.forEach((type: IFilterType): void => {
      options[type.description] = {
        name: type.description,
        selected: type.description === this.filter?.filterType,
        disabled: false,
      };
    });
    return options;
  }

  @Input() set setColDef(colDefs: ColDef[]) {
    const copies = colDefs.map((col: ColDef): ColDef => ({...col}));
    copies.forEach((col: ColDef): void => {
      if (col.headerName === '') {
        col.headerName = getHumanReadableIdentifier(col, 'headerName', 'field');
      }
    });
    this.colDefs = copies.sort((a: ColDef, b: ColDef): number => a.headerName > b.headerName ? 1 : -1);
    if (this.filter && (!this.filter.column || !this.filter.filterType)) {
      this.setSelectedColumnByFilter();
    }
  }

  @Input() set setFilter(inputFilter: IFilter | IFilterGroup) {
    this.filter = undefined;
    this.rangeMin = '';
    this.rangeMax = '';
    if (inputFilter) {
      this.filter = getDeepCopyOfObject(inputFilter as IFilter);
      this.parseRangeValuesFromFilter();
      this.setSelectedColumnByFilter();
    }
  }

  onColumnChange(selected: Record<string, ISelectOption>): void {
    const selectedColumn = Object.keys(selected).find((key: string): boolean => selected[key].selected);
    if (!selectedColumn) {
      return;
    }
    this.filter.column = selectedColumn;
    this.filter.columnType = resolveAdvancedFilterColumnType(this.filter.column);
    this.setFilterType();
    this.store.dispatch(setFilterAfterValueChange({filter: this.filter}));
  }

  onDeleteItemClick(): void {
    if (!this.allowRemove) {
      return;
    }
    this.store.dispatch(deleteAdvancedFilterGroupFilter({filter: this.filter}));
  }

  onFilterTypeChange(selected: Record<string, ISelectOption>): void {
    const selectedType = Object.keys(selected).find((key: string): boolean => selected[key].selected);
    if (!selectedType) {
      return;
    }
    this.filter.filterType = selectedType;
    this.filter.value = '';
    this.rangeMin = '';
    this.rangeMax = '';
    this.store.dispatch(setFilterAfterValueChange({filter: this.filter}));
  }

  onValueChange(): void {
    this.store.dispatch(setFilterAfterValueChange({filter: this.filter}));
  }

  /**
   * Persists the between/from-to values as a comma-separated min,max string.
   */
  onRangeValueChange(): void {
    this.filter.value = `${this.rangeMin},${this.rangeMax}`;
    this.store.dispatch(setFilterAfterValueChange({filter: this.filter}));
  }

  /**
   * Splits a stored between value into the from/to inputs.
   */
  private parseRangeValuesFromFilter(): void {
    if (!this.filter || this.filter.filterType !== FilterTypeTypesenseEnum.Between) {
      this.rangeMin = '';
      this.rangeMax = '';
      return;
    }
    const parts = String(this.filter.value ?? '').split(',');
    this.rangeMin = parts[0]?.trim() ?? '';
    this.rangeMax = parts[1]?.trim() ?? '';
  }

  /**
   * Resolves the column and filter type the dropdowns should display.
   *
   * A filter row with no column yet is a new row, so it is defaulted to the
   * first available column and that default is persisted. A row whose saved
   * column is absent from the current `colDefs` keeps its column: it belongs
   * to a grid with a different column set, and rewriting it here would
   * silently replace the user's criteria.
   */
  private setSelectedColumnByFilter(): void {
    if (!this.filter || !this.colDefs?.length) {
      return;
    }
    const found = this.colDefs.find((col: ColDef): boolean => col.field === this.filter.column);
    const hadColumn: boolean = !!this.filter.column;
    const hadFilterType: boolean = !!this.filter.filterType;

    if (found) {
      this.filter.column = found.field;
      this.filter.columnType = resolveAdvancedFilterColumnType(found.field, this.filter.columnType);
    } else if (hadColumn) {
      this.filter.columnType = resolveAdvancedFilterColumnType(this.filter.column, this.filter.columnType);
    } else {
      const firstColumn = this.colDefs[0];
      this.filter.column = firstColumn.field;
      this.filter.columnType = resolveAdvancedFilterColumnType(firstColumn.field);
    }
    this.setFilterType();

    const didDefaultColumn: boolean = !hadColumn && !!this.filter.column;
    const didDefaultFilterType: boolean = !hadFilterType && !!this.filter.filterType;
    if (didDefaultColumn || didDefaultFilterType) {
      this.store.dispatch(setFilterAfterValueChange({filter: this.filter}));
    }
  }

  private setFilterType(): void {
    this.filterTypes = [];
    let baseFilterTypes: IFilterType[] = [];
    switch (this.filter.columnType) {
      case 'boolean':
        baseFilterTypes = [...this.filterTypeBoolean];
        break;
      case 'date':
        baseFilterTypes = [...this.filterTypeDate];
        break;
      case 'number':
        baseFilterTypes = [...this.filterTypeNumber];
        break;
      case 'string[]':
        baseFilterTypes = [...this.filterTypeStringArray];
        break;
      default:
      case 'string':
        baseFilterTypes = [...this.filterTypeString];
        break;
    }
    this.filterTypes = [...baseFilterTypes, ...FilterTypesPresence];
    const typeExists = this.filterTypes.some(
      (type: IFilterType): boolean => type.description === this.filter.filterType,
    );
    if (!this.filter.filterType || !typeExists) {
      this.filter.filterType = this.filterTypes[0]?.description;
    }
  }

}
